/* eslint-disable no-underscore-dangle */
import { Tenant, User } from '@innovation-os-ng/core/models';
import { ReplaySubject } from 'rxjs';
import { take } from 'rxjs/operators';
import {
  AnalyticsErrorEvent,
  AnalyticsGenericEvent,
  AnalyticsPageViewEvent,
  AnalyticsTracker,
  Tracker
} from '../contracts/analytics-tracker';
import { TrackedPermissions } from '../services/tracking-permission.service';
import { TenantCustomProperties, UserCustomProperties } from '../services/tracking-setup.service';
import { TrackingService } from '../services/tracking.service';

declare let window: { _ga: any };
export class GoogleTracker implements AnalyticsTracker {
  public readonly type = Tracker.GOOGLE_ANALYTICS;

  private trackerReadyStateChanged: ReplaySubject<void> = new ReplaySubject();

  // pass user and tenant information here and remove tracking guard
  constructor(
    trackingService: TrackingService,
    user?: User,
    tenant?: Tenant,
    customProperties?: {
      userCustomProperties?: UserCustomProperties;
      tenantCustomProperties?: TenantCustomProperties;
    }
  ) {
    this.initGoogleAnalytics();
    this.trackerReadyStateChanged.pipe(take(1)).subscribe(() => {
      // Start listening to all the events from the tracking service
      trackingService.onSetUser.subscribe((event) => this.setUser(event.user, event.customProperties));
      trackingService.onSetTenant.subscribe((event) => this.setTenant(event.tenant, event.customProperties));
      trackingService.onTrackError.subscribe((event) => this.trackError(event.event, event.permissions));
      trackingService.onTrackPageView.subscribe((event) => this.trackPageView(event.event, event.permissions));
      trackingService.onTrackEvent.subscribe((event) => this.trackEvent(event.event, event.permissions));
      // Now that all is wired internally, we can trigger some first events
      this.setUser(user, customProperties?.userCustomProperties);
      this.setTenant(tenant, customProperties?.tenantCustomProperties);
    });
  }

  private trackPageView(event: AnalyticsPageViewEvent, permissions?: TrackedPermissions) {
    let pageViewOptions = {
      dimension1: event.spaceId,
      dimension2: event.userId,
      dimension3: event.tenantName,
      dimension7: event.tenantId,
      ...(permissions ? this.processTrackedPermissions(permissions) : [])
    };
    /** ga:  Track the visitor's page view **/
    // set custom dimension and metrics
    if (event.customProperties) {
      pageViewOptions = {
        ...pageViewOptions,
        ...event.customProperties
      };
    }
    window._ga('set', 'page', event.url);
    window._ga('set', 'title', ''); // Setting title to empty makes it not_set
    window._ga('send', 'pageview', pageViewOptions);
  }

  /**
   * Tracking Custom User Actions
   * Button Clicks / Event listeners
   * Form Submitted
   */
  private trackEvent(event: AnalyticsGenericEvent, permissions?: TrackedPermissions) {
    let eventOptions = {
      eventCategory: event.eventCategory,
      eventAction: event.eventAction,
      eventLabel: event.eventLabel,
      eventValue: 0,
      dimension1: event.spaceId,
      dimension2: event.userId,
      dimension3: event.tenantName,
      dimension6: event.eventId,
      dimension7: event.tenantId,
      ...(permissions ? this.processTrackedPermissions(permissions) : [])
    };
    // set custom dimension and metrics
    if (event.customProperties) {
      eventOptions = {
        ...eventOptions,
        ...event.customProperties
      };
    }
    /** Track a user event (optional) **/
    window._ga('send', 'event', eventOptions);
  }

  private trackError(event: AnalyticsErrorEvent, permissions?: TrackedPermissions) {
    const eventOptions = {
      eventCategory: 'ERROR',
      eventAction: event.requestUrl,
      eventLabel: event.responseCode,
      eventValue: 0,
      dimension4: event.requestId,
      dimension5: event.windowLocation,
      ...(permissions ? this.processTrackedPermissions(permissions) : [])
    };
    /** Track a user event (optional) **/
    window._ga('send', 'event', eventOptions);
  }

  // implementing a contract. Google Analytics does not work with tenant concept. Ignore
  private setTenant(tenant: Tenant, customProperties?: TenantCustomProperties) {
    // Do nothing
  }

  // implementing a contract. Google Analytics does need all the params. Ignore some
  private setUser(user: User, customProperties?: UserCustomProperties) {
    if (!user) {
      return;
    }
    window._ga('set', 'userId', user.sub);
    window._ga('set', 'dimension2', user.sub);
    if (customProperties) {
      // here it seems obvious to set it global as we are setting it
      // for particular user and also this can be overidden in event track and page view track
      window._ga('set', customProperties);
    }
  }

  private processTrackedPermissions(permissions: TrackedPermissions) {
    const tenantLvl: Array<string> = Object.keys(permissions.tenantLvl).filter((key) => permissions.tenantLvl[key]);
    const spaceLvl: Array<string> = Object.keys(permissions.spaceLvl).filter((key) => permissions.spaceLvl[key]);
    // Push empty strings to add space at the end of the join
    tenantLvl.push('');
    spaceLvl.push('');
    return {
      dimension9: tenantLvl.join(' '),
      dimension10: spaceLvl.join(' ')
    };
  }

  private initGoogleAnalytics(): ReplaySubject<void> {
    if (typeof window._ga !== 'undefined') {
      return;
    }
    /**
     * Load and instantiate the google tracking snippet to start tracking.
     * Do not add any scripts into index.html or anywhere outside of this.
     * We have the execution of this very init() guarded by the users Cookie
     * and Tracking choices and must ensure to adhere to the policies around
     * enabling the trackers only if allowed.
     *
     * CLOUD-17774 erased the actual instantiation of the outdated
     * "Universal Analytics" here and this class is since not functional.
     *
     * The class remains hoping it is easy to revive should we opt for Google
     * Tracking again and ideally we need to simply just load and execute the
     * Google Analytics V4 or newer below this comment plus possibly update some
     * of the _ga calls above.
     *  */

    /* eslint-disable-next-line no-console */
    console.warn('GA not supported');
  }
}
